// Apothecary brand kit — shared marks and atoms.
// Ported verbatim from the Stillhouse design system (window.APO).
import React from "react";
import { C, F } from "./tokens";

// Re-exported so screens can pull tokens + atoms from one module, mirroring the
// design's single `window.APO` surface.
export { C, F } from "./tokens";

// ── Marks ──────────────────────────────────────────────────────

// log-ish ms→y mapping that matches the graduation positions
function msToY(ms: number): number {
  const pts: [number, number][] = [
    [1, 168],
    [4, 138],
    [8, 118],
    [16, 90],
    [32, 62],
  ];
  const l = Math.log2(Math.max(0.5, ms));
  for (let i = 0; i < pts.length - 1; i++) {
    const [m1, y1] = pts[i];
    const [m2, y2] = pts[i + 1];
    const l1 = Math.log2(m1);
    const l2 = Math.log2(m2);
    if (l >= l1 && l <= l2) return y1 + ((l - l1) / (l2 - l1)) * (y2 - y1);
  }
  // extrapolate
  const [m1, y1] = pts[pts.length - 2];
  const [m2, y2] = pts[pts.length - 1];
  const l1 = Math.log2(m1);
  const l2 = Math.log2(m2);
  return y1 + ((l - l1) / (l2 - l1)) * (y2 - y1);
}

export type Competitor = { ms: number; label?: string; color: string };

// Hero mark — chubby mason jar with ms graduations. Liquid reads 4 ms.
export function Vial({
  size = 200,
  color = C.glow,
  ink = C.bone,
  variant = "full",
  strokeWidth = 2,
  competitors = [],
}: {
  size?: number;
  color?: string;
  ink?: string;
  variant?: "full" | "plain";
  strokeWidth?: number;
  competitors?: Competitor[];
}) {
  const showLabels = variant === "full";
  // Match the chunky proportions of the mini logo — wider than tall
  const w = showLabels ? size * 1.0 : size * 0.675;
  const h = size;
  const marks: [number, string, boolean][] = [
    [62, "32", false],
    [90, "16", false],
    [118, "8", false],
    [138, "4", true],
    [168, "1", false],
  ];
  const id = `v-${size}-${variant}-${color.replace("#", "")}`;
  // Mason jar body — chubby: body width 110, gently rounded shoulders + base
  const jarPath =
    "M28 32 Q10 32 10 47 L10 180 Q10 192 22 192 L108 192 Q120 192 120 180 L120 47 Q120 32 102 32 Z";

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${showLabels ? 200 : 135} 200`}
      fill="none"
      aria-label="MoonShine · latency distilled to 4 ms"
    >
      {/* Two-piece lid — band almost the full width of the body */}
      <rect x="18" y="4" width="94" height="6" rx="1" fill={ink} />
      <rect x="14" y="10" width="102" height="22" rx="1.5" fill={ink} />
      <line x1="20" y1="16" x2="110" y2="16" stroke={C.surface} strokeWidth="0.9" opacity="0.55" />
      <line x1="20" y1="21" x2="110" y2="21" stroke={C.surface} strokeWidth="0.9" opacity="0.55" />
      <line x1="20" y1="26" x2="110" y2="26" stroke={C.surface} strokeWidth="0.9" opacity="0.55" />
      {/* Puddle shadow under the jar */}
      <ellipse cx="65" cy="196" rx="60" ry="6" fill={color} opacity="0.18" />
      {/* Glass body */}
      <path d={jarPath} stroke={ink} strokeWidth={strokeWidth} strokeLinejoin="round" />
      {/* Liquid fill clipped to body — MoonShine + competitor strata */}
      <defs>
        <clipPath id={id}>
          <path d={jarPath} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        {(() => {
          const sorted = [...competitors].sort((a, b) => a.ms - b.ms);
          let prevY = 138;
          const out: React.ReactNode[] = [];
          sorted.forEach(({ ms, color: cc }, i) => {
            const y = msToY(ms);
            out.push(
              <rect key={`band-${i}`} x="8" y={y} width="114" height={prevY - y} fill={cc} opacity="0.95" />,
            );
            prevY = y;
          });
          return out;
        })()}
        <rect x="8" y="138" width="114" height="60" fill={color} opacity="0.95" />
      </g>
      {/* MoonShine meniscus — the brand-defining 4 ms line */}
      <line x1="10" y1="138" x2="120" y2="138" stroke={color} strokeWidth="6" strokeLinecap="round" opacity="0.35" />
      <line x1="10" y1="138" x2="120" y2="138" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      {/* Competitor menisci */}
      {competitors.map(({ ms, color: cc }, i) => {
        const y = msToY(ms);
        return <line key={`men-${i}`} x1="12" y1={y} x2="118" y2={y} stroke={cc} strokeWidth="1.8" strokeLinecap="round" />;
      })}
      {/* Graduations to the right of the jar */}
      {marks.map(([y, , main]) => (
        <line
          key={y}
          x1="120"
          x2={main ? "132" : "126"}
          y1={y}
          y2={y}
          stroke={main ? color : ink}
          strokeWidth={main ? 2 : 1}
          opacity={main ? 1 : 0.55}
        />
      ))}
      {showLabels && (
        <g fontFamily={F.mono} fontWeight="600">
          {marks.map(([y, label, main]) => (
            <text key={y} x="140" y={y + 3.5} fontSize={main ? 13 : 9} fill={main ? color : ink} opacity={main ? 1 : 0.45}>
              {label}
              {main ? " ms" : ""}
            </text>
          ))}
        </g>
      )}
    </svg>
  );
}

// Tiny inline mark for nav / favicon contexts — mason jar silhouette
export function VialIcon({ size = 24, color = C.glow, ink = C.fgDark }: { size?: number; color?: string; ink?: string }) {
  const id = `vi-${size}-${color.replace("#", "")}-${ink.replace("#", "")}`;
  const body = "M11 12 Q5 12 5 17 L5 43 Q5 47 9 47 L31 47 Q35 47 35 43 L35 17 Q35 12 29 12 Z";
  return (
    <svg width={size * 0.82} height={size} viewBox="0 0 40 50" fill="none">
      {/* Two-piece lid */}
      <rect x="7" y="2" width="26" height="2" rx="0.4" fill={ink} />
      <rect x="6" y="4" width="28" height="8" rx="0.6" fill={ink} />
      <line x1="8" y1="7" x2="32" y2="7" stroke={C.surface} strokeWidth="0.5" opacity="0.55" />
      <line x1="8" y1="10" x2="32" y2="10" stroke={C.surface} strokeWidth="0.5" opacity="0.55" />
      {/* Glass body */}
      <path d={body} stroke={ink} strokeWidth="1.5" strokeLinejoin="round" />
      <defs>
        <clipPath id={id}>
          <path d={body} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <rect x="4" y="33" width="32" height="16" fill={color} opacity="0.95" />
      </g>
      <line x1="5" y1="33" x2="35" y2="33" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// Wordmark — used in nav bars and lockups
export function Wordmark({ size = 22, color, dotColor = C.glow }: { size?: number; color?: string; dotColor?: string }) {
  return (
    <span
      style={{
        fontFamily: F.display,
        fontWeight: 500,
        fontSize: size,
        color: color || "inherit",
        letterSpacing: "-0.025em",
      }}
    >
      moonshine<span style={{ color: dotColor }}>.</span>
    </span>
  );
}

export function Logo({
  wordmarkSize = 22,
  vialSize = 24,
  color,
  dotColor = C.glow,
  ink = C.fgDark,
}: {
  wordmarkSize?: number;
  vialSize?: number;
  color?: string;
  dotColor?: string;
  ink?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <VialIcon size={vialSize} color={dotColor} ink={ink} />
      <Wordmark size={wordmarkSize} color={color} dotColor={dotColor} />
    </span>
  );
}

// ── Atoms ──────────────────────────────────────────────────────
export function Pill({
  children,
  color = C.glow,
  bg,
  border,
  glow = false,
  size = "sm",
  mono = true,
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
  border?: string;
  glow?: boolean;
  size?: "sm" | "md";
  mono?: boolean;
}) {
  const pad = size === "sm" ? "4px 10px" : "6px 14px";
  const fs = size === "sm" ? 11 : 12;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: pad,
        fontSize: fs,
        fontFamily: mono ? F.mono : F.ui,
        fontWeight: 600,
        letterSpacing: "0.08em",
        color,
        background: bg || `${color}18`,
        border: `1px solid ${border || color + "44"}`,
        boxShadow: glow ? `0 0 12px ${color}55` : "none",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

export function Dot({ color, size = 7, glow = true }: { color: string; size?: number; glow?: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        boxShadow: glow ? `0 0 8px ${color}` : "none",
      }}
    />
  );
}

export function Btn({
  children,
  primary,
  ghost,
  size = "md",
  onClick,
  style,
  type = "button",
}: {
  children: React.ReactNode;
  primary?: boolean;
  ghost?: boolean;
  size?: "md" | "lg";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  style?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
}) {
  const pad = size === "lg" ? "12px 22px" : "8px 16px";
  const fs = size === "lg" ? 14 : 13;
  let bg: string, color: string, border: string;
  if (primary) {
    bg = C.glow;
    color = C.ink;
    border = C.glow;
  } else if (ghost) {
    bg = "transparent";
    color = C.fgDark;
    border = C.line;
  } else {
    bg = C.surfaceHi;
    color = C.fgDark;
    border = C.line;
  }
  return (
    <button
      type={type}
      style={{
        padding: pad,
        fontSize: fs,
        fontFamily: F.ui,
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${border}`,
        cursor: "pointer",
        letterSpacing: "-0.005em",
        ...style,
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// Grid pattern background for dark surfaces
export function GridBG({ opacity = 0.5, size = 32, color = C.line }: { opacity?: number; size?: number; color?: string }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `linear-gradient(${color} 1px, transparent 1px),
                          linear-gradient(90deg, ${color} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
        pointerEvents: "none",
      }}
    />
  );
}

// Mini latency meter — horizontal bar gauging ms (used in cards)
export function LatencyMeter({
  ms = 4,
  max = 32,
  color = C.glow,
  width = 120,
}: {
  ms?: number;
  max?: number;
  color?: string;
  width?: number | string;
}) {
  const pct = Math.max(0.06, Math.min(1, ms / max));
  return (
    <div style={{ width, height: 6, background: C.surfaceHi, borderRadius: 2, position: "relative", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${pct * 100}%`,
          background: color,
          boxShadow: `0 0 8px ${color}88`,
        }}
      />
      {/* tick marks at 4/8/16 ms */}
      {[4, 8, 16].map((t) => (
        <span
          key={t}
          style={{
            position: "absolute",
            left: `${(t / max) * 100}%`,
            top: 0,
            bottom: 0,
            width: 1,
            background: "rgba(255,255,255,0.15)",
          }}
        />
      ))}
    </div>
  );
}
